import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import { useValidation } from '@hooks/auth/useValidation'
import { AuthService } from 'services/auth/AuthService'
import { useTelemetry } from '@hooks/data/useTelemetry'
import { useToast } from '@hooks/ui/useToast'
import { useTranslation } from 'react-i18next'
import InfoToast from '@components/shared/Toast/InfoToast'

// Types
interface AuthFormErrors {
  email: boolean
  password: boolean
  confirmPassword: boolean
  code: boolean
  codeInputErrors: string
}

interface AuthFormState {
  email: string
  password: string
  confirmPassword: string
  code: string
  isLoading: boolean
  errors: AuthFormErrors
  cameFromSignup: boolean
  name: string
  successDescription: string | null
  warningDescription: string | null
}

interface AuthFormContextType extends AuthFormState {
  // Setters
  setEmail: (email: string | ((prev: string) => string)) => void
  setPassword: (password: string | ((prev: string) => string)) => void
  setConfirmPassword: (
    confirmPassword: string | ((prev: string) => string)
  ) => void
  setCode: (code: string | ((prev: string) => string)) => void
  setIsLoading: (isLoading: boolean) => void
  setName: (name: string | ((prev: string) => string)) => void
  setSuccessDescription: (description: string | null) => void
  setWarningDescription: (description: string | null) => void
  // Error setters
  setEmailError: (error: boolean | ((prev: boolean) => boolean)) => void
  setPasswordError: (error: boolean | ((prev: boolean) => boolean)) => void
  setConfirmPasswordError: (
    error: boolean | ((prev: boolean) => boolean)
  ) => void
  setCodeError: (error: boolean | ((prev: boolean) => boolean)) => void
  setCodeInputErrors: (error: string) => void

  // Validation helpers
  validateEmail: (email?: string) => boolean
  validatePassword: (password?: string) => boolean
  validateCode: (code?: string) => boolean

  // Computed values
  validLength: boolean
  validCharacters: boolean

  // Actions
  resendCode: (isForgotPassword?: boolean) => Promise<void>
  clearAuthForm: () => void
  clearErrors: () => void
  resetAll: () => void

  // Navigation tracking
  setCameFromSignup: (value: boolean) => void
}

// Context
const AuthFormContext = createContext<AuthFormContextType | undefined>(
  undefined
)

// Provider component
interface AuthFormProviderProps {
  children: ReactNode
}

export const AuthFormProvider: React.FC<AuthFormProviderProps> = ({
  children,
}) => {
  // Third-party hooks
  const { t } = useTranslation()
  const { displayToast } = useToast()
  const { track } = useTelemetry()
  const { validate, validatePasswordStrength } = useValidation()

  // Local state
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [code, setCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [successDescription, setSuccessDescription] = useState<string | null>(
    null
  )
  const [warningDescription, setWarningDescription] = useState<string | null>(
    null
  )
  const [errors, setErrors] = useState<AuthFormErrors>({
    email: false,
    password: false,
    confirmPassword: false,
    code: false,
    codeInputErrors: '',
  })
  const [cameFromSignup, setCameFromSignup] = useState(false)

  // Computed values
  const { validLength, validCharacters } = useMemo(
    () => validatePasswordStrength(password),
    [password, validatePasswordStrength]
  )

  // Error setters
  const setEmailError = useCallback(
    (error: boolean | ((prev: boolean) => boolean)) => {
      setErrors((prev) => ({
        ...prev,
        email: typeof error === 'function' ? error(prev.email) : error,
      }))
    },
    []
  )

  const setPasswordError = useCallback(
    (error: boolean | ((prev: boolean) => boolean)) => {
      setErrors((prev) => ({
        ...prev,
        password: typeof error === 'function' ? error(prev.password) : error,
      }))
    },
    []
  )

  const setConfirmPasswordError = useCallback(
    (error: boolean | ((prev: boolean) => boolean)) => {
      setErrors((prev) => ({
        ...prev,
        confirmPassword:
          typeof error === 'function' ? error(prev.confirmPassword) : error,
      }))
    },
    []
  )

  const setCodeError = useCallback(
    (error: boolean | ((prev: boolean) => boolean)) => {
      setErrors((prev) => ({
        ...prev,
        code: typeof error === 'function' ? error(prev.code) : error,
      }))
    },
    []
  )

  const setCodeInputErrors = useCallback((error: string) => {
    setErrors((prev) => ({ ...prev, codeInputErrors: error }))
  }, [])

  // Validation helpers
  const validateEmail = useCallback(
    (emailToValidate?: string) => {
      const emailValue = emailToValidate ?? email
      const emailError = validate('email', emailValue)
      if (emailError) {
        setEmailError(true)
        return false
      }
      setEmailError(false)
      return true
    },
    [email, validate, setEmailError]
  )

  const validatePassword = useCallback(
    (passwordToValidate?: string) => {
      const passwordValue = passwordToValidate ?? password
      const passwordError = validate('password', passwordValue)
      if (passwordError || !validLength || !validCharacters) {
        setPasswordError(true)
        return false
      }
      setPasswordError(false)
      return true
    },
    [password, validate, validLength, validCharacters, setPasswordError]
  )

  const validateCode = useCallback(
    (codeToValidate?: string) => {
      const codeValue = codeToValidate ?? code
      if (!codeValue.trim()) {
        setCodeError(true)
        setCodeInputErrors(t('enterYourCode'))
        return false
      }
      setCodeError(false)
      setCodeInputErrors('')
      return true
    },
    [code, t, setCodeError, setCodeInputErrors]
  )

  // Actions
  const resendCode = useCallback(
    async (isForgotPassword?: boolean) => {
      if (!email) return

      try {
        setCodeError(false)
        setWarningDescription(null)
        if (isForgotPassword) {
          await AuthService.initiateResetPassword(email)
        } else {
          await AuthService.resendSignUpCode(email)
        }
        setSuccessDescription(t('codeResent'))
        track(
          isForgotPassword
            ? 'password_reset_code_resent'
            : 'registration_code_resent',
          { email }
        )
      } catch (error: any) {
        setSuccessDescription(null)
        setWarningDescription(null)
        if (error.name === 'LimitExceededException') {
          setWarningDescription(t('codeLimitExceeded'))
        } else {
          setCodeError(true)
          setCodeInputErrors(t('codeInvalid'))
        }
        console.error('Error resending code:', error)
      }
    },
    [email, track, displayToast, t, setCodeError, setCodeInputErrors]
  )

  const clearAuthForm = useCallback(() => {
    setEmail('')
    setPassword('')
    setConfirmPassword('')
    setCode('')
    setIsLoading(false)
    setErrors({
      email: false,
      password: false,
      confirmPassword: false,
      code: false,
      codeInputErrors: '',
    })
  }, [])

  const clearErrors = useCallback(() => {
    setErrors({
      email: false,
      password: false,
      confirmPassword: false,
      code: false,
      codeInputErrors: '',
    })
  }, [])

  // Resets all local auth form-related state
  const resetAll = useCallback(() => {
    clearAuthForm()
    setName('')
    setCameFromSignup(false)
  }, [clearAuthForm])

  // Context value
  const contextValue: AuthFormContextType = {
    // State
    email,
    password,
    confirmPassword,
    code,
    isLoading,
    errors,
    name,
    successDescription,
    warningDescription,
    // Setters
    setEmail,
    setPassword,
    setConfirmPassword,
    setCode,
    setIsLoading,
    setName,
    setSuccessDescription,
    setWarningDescription,
    // Error setters
    setEmailError,
    setPasswordError,
    setConfirmPasswordError,
    setCodeError,
    setCodeInputErrors,

    // Validation helpers
    validateEmail,
    validatePassword,
    validateCode,

    // Computed values
    validLength,
    validCharacters,

    // Actions
    resendCode,
    clearAuthForm,
    clearErrors,
    resetAll,

    // Navigation tracking
    cameFromSignup,
    setCameFromSignup,
  }

  return (
    <AuthFormContext.Provider value={contextValue}>
      {children}
    </AuthFormContext.Provider>
  )
}

// Hook to use the context
export const useAuthForm = (): AuthFormContextType => {
  const context = useContext(AuthFormContext)
  if (context === undefined) {
    throw new Error('useAuthForm must be used within an AuthFormProvider')
  }
  return context
}
